import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { AuctionHouse, AuctionHouseStats } from '../types/auctionHouse.types';

export const auctionHouseService = {
  // Get auction house by subdomain
  async getBySubdomain(subdomain: string): Promise<AuctionHouse | null> {
    const housesRef = collection(db, 'auctionHouses');
    const q = query(housesRef, where('subdomain', '==', subdomain));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    const docData = snapshot.docs[0];
    const data = docData.data();
    
    return {
      id: docData.id,
      ...data,
      joinedDate: data.joinedDate?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as AuctionHouse;
  },

  // Get auction house by ID
  async getById(id: string): Promise<AuctionHouse | null> {
    const docRef = doc(db, 'auctionHouses', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) return null;
    
    const data = docSnap.data();
    
    return {
      id: docSnap.id,
      ...data,
      joinedDate: data.joinedDate?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as AuctionHouse;
  },

  // Get all active auction houses
  async getAll(): Promise<AuctionHouse[]> {
    const housesRef = collection(db, 'auctionHouses');
    const q = query(housesRef, where('isActive', '==', true), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(docData => {
      const data = docData.data();
      return {
        id: docData.id,
        ...data,
        joinedDate: data.joinedDate?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as AuctionHouse;
    });
  },

  // Create new auction house
  async create(auctionHouse: Omit<AuctionHouse, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const housesRef = collection(db, 'auctionHouses');
    const newDocRef = doc(housesRef);
    
    await setDoc(newDocRef, {
      ...auctionHouse,
      totalAuctions: 0,
      totalLots: 0,
      totalPredictions: 0,
      totalUsers: 0,
      customDomainVerified: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    
    return newDocRef.id;
  },

  // Update auction house
  async update(id: string, updates: Partial<AuctionHouse>): Promise<void> {
    const docRef = doc(db, 'auctionHouses', id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  },

  // Get auction house stats
  async getStats(auctionHouseId: string): Promise<AuctionHouseStats | null> {
    const statsRef = doc(db, 'auctionHouseStats', auctionHouseId);
    const statsSnap = await getDoc(statsRef);
    
    if (!statsSnap.exists()) return null;
    
    const data = statsSnap.data();
    
    return {
      ...data,
      lastCalculated: data.lastCalculated?.toDate() || new Date(),
    } as AuctionHouseStats;
  },

  // Detect current auction house from URL
getCurrentAuctionHouse(): string | null {
  const hostname = window.location.hostname;
  
  // Production: Check if we're on a subdomain
  if (hostname.includes('.auctionringer.com') && !hostname.startsWith('www')) {
    const subdomain = hostname.split('.')[0];
    console.log('🏠 Detected subdomain:', subdomain);
    return subdomain;
  }
  
  // Localhost: Use query param ?portal=schmidt
  if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) {
    const params = new URLSearchParams(window.location.search);
    const portal = params.get('portal');
    console.log('🏠 Detected portal param:', portal);
    return portal;
  }
  
  // Default to aggregator (no portal)
  console.log('🏠 No portal detected - showing aggregator');
  return null;
}
};